import {
  TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import * as Sentry from "@sentry/react"
import { Component, ErrorInfo, ReactNode } from "react"

interface ErrorBoundaryInternalProps {
  children: ReactNode
  fallback?: ReactNode | ((reset: () => void) => ReactNode)
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  translate: (key: TranslationKeys) => string
  resetKey?: string | number
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundaryInternal extends Component<
  ErrorBoundaryInternalProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryInternalProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error }
  }

  componentDidUpdate(prevProps: ErrorBoundaryInternalProps): void {
    // Reset error state when resetKey changes
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null })
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service here
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, errorInfo)
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    })
    this.props.onError?.(error, errorInfo)
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        if (typeof this.props.fallback === "function") {
          return this.props.fallback(this.reset)
        }
        return this.props.fallback
      }

      return (
        <div className="flex h-full items-center justify-center bg-grey3">
          <div className="text-center">
            <h2 className="pb-M text-lg font-semibold">
              {this.props.translate("ERROR/SOMETHING_WENT_WRONG")}
            </h2>
            <p className="text-sm text-grey5">
              {this.state.error?.message ||
                this.props.translate("ERROR/UNEXPECTED_ERROR_OCCURED")}
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode | ((reset: () => void) => ReactNode)
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  resetKey?: string | number
}

export function ErrorBoundary({
  children,
  fallback,
  onError,
  resetKey,
}: ErrorBoundaryProps) {
  const { translate } = useTranslation()

  return (
    <ErrorBoundaryInternal
      translate={translate}
      fallback={fallback}
      onError={onError}
      resetKey={resetKey}
    >
      {children}
    </ErrorBoundaryInternal>
  )
}
